import { useEffect, useState } from 'react';

import RichText from 'components/shared/richtext';
import renderSections from 'components/sections/render';
import useSWR from 'swr';

import RelatedProducts from 'components/sections/relatedProducts/relatedProducts';
import trackKlavioProductView from 'lib/utils/trackKlavioProductView';
import { parseShopifyGid } from 'lib/shopify/shopifyGid';
import Expander from 'components/shared/expander/expander';
import getShopifyData from 'lib/shopify/getShopifyData';
import EvesReviews from 'components/shared/eves-reviews';
import styles from './productBundle.module.scss';
import ProductBundleInfo from './productBundleInfo';
import ProductGallery from '../product/productGallery/productGallery';

const PageProductBundle = ({ data, locale, resources }) => {
  const [automaticRelatedProducts, setAutomaticRelatedProducts] = useState([]);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [combinedProductsPrices, setCombinedProductsPrices] = useState({});
  const stieglitzProductBundlePrice = 59.95;

  const {
    thumbnailImage,
    productPictures,
    products,
    additionalSections,
    productAttributes,
    productVideos,
    bundleDiscount,
  } = data;

  const { ingredientsTitle, disclaimerText, directionOfUseTitle } = resources.productpage;
  const { relatedProductsDefaultTitle, reviewsTitle } = resources.secondpartproductpage;
  const otherSections = additionalSections.filter(
    section => section.type !== 'SectionRelatedProducts'
  );
  const relatedProductsSection = additionalSections.find(
    section => section.type === 'SectionRelatedProducts'
  );

  const reviewProducts = products?.filter(product => !product.slug.includes('dispenser'));

  const fetcher = async () => {
    const ids = products.map(
      prod => `gid://shopify/Product/${prod.shopifyData?.variants?.[0]?.product_id}`
    );

    const actualShopifyProducts = await getShopifyData({
      productIds: ids,
      locale,
    });
    return actualShopifyProducts;
  };

  const swrOptions = {
    refreshInterval: 60000,
  };

  const { data: updatedProductData } = useSWR(products, fetcher, swrOptions);

  const extractCombinedProductsPrices = productsArray => {
    let originalCombinedPrice = productsArray
      ?.reduce(
        (previousItem, currentItem) => previousItem + parseFloat(currentItem.productPrice || 0),
        0
      )
      .toFixed(2);

    const doesComaparePriceExist = productsArray?.find(item => !!item?.productCompareAtPrice);

    const isProductStieglitzInBundle =
      !!products.filter(product => product.slug === 'keune-stieglitz-giftbox').length > 0;

    let combinedCompareAtPrice = doesComaparePriceExist
      ? productsArray
          .reduce(
            (previousItem, currentItem) =>
              previousItem +
              parseFloat(currentItem.productCompareAtPrice || currentItem.productPrice || 0),
            0
          )
          .toFixed(2)
      : null;

    const salePercentage = doesComaparePriceExist
      ? Math.round(100 - (originalCombinedPrice / combinedCompareAtPrice) * 100)
      : null;

    const outOfStock = productsArray.some(product => !!product?.outOfStock);
    const currencyCode = productsArray[0]?.currencyCode; // first product in the array

    if (bundleDiscount) {
      combinedCompareAtPrice = originalCombinedPrice;

      const roundToTwo = num => +`${Math.round(`${num}e+2`)}e-2`;
      originalCombinedPrice = productsArray?.reduce(
        (previousItem, currentItem) =>
          previousItem +
          (currentItem?.productPrice
            ? roundToTwo(
                currentItem?.productPrice - currentItem?.productPrice * (bundleDiscount / 100)
              )
            : 0),
        bundleDiscount >= 30 ? 0.01 : 0 // starting point. For some reason the 0.01 is constantly lost during calculations
      );
    }

    setCombinedProductsPrices({
      price: isProductStieglitzInBundle
        ? stieglitzProductBundlePrice
        : originalCombinedPrice || null,
      compareAtPrice: isProductStieglitzInBundle ? originalCombinedPrice : combinedCompareAtPrice,
      salePercentage,
      outOfStock,
      currencyCode,
    });
  };

  useEffect(() => {
    // set the initial prices for params
    if (!products) return;
    const formatedProducts = products.map(product => ({
      productPrice: product.shopifyData?.variants?.[0]?.price,
      productCompareAtPrice: product.shopifyData?.variants?.[0]?.compareAtPrice,
      outOfStock: product.shopifyData?.variants?.[0]?.outOfStock,
      currencyCode: product.shopifyData?.variants?.[0]?.currencyCode,
    }));
    extractCombinedProductsPrices(formatedProducts);
  }, [products]);

  useEffect(() => {
    // update the prices from shopify data
    if (!updatedProductData) return;
    setShopifyProducts(updatedProductData);
    extractCombinedProductsPrices(updatedProductData.flat());
  }, [updatedProductData, products]);

  useEffect(() => {
    async function retrieveSimilarProducts() {
      const productsResponse = await fetch('/api/get-related-products', {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify({
          attributes: productAttributes,
          locale,
        }),
      });
      try {
        if (productsResponse.ok) {
          const relatedProducts = await productsResponse.json();
          setAutomaticRelatedProducts(relatedProducts);
        }
        return null;
      } catch (e) {
        return productsResponse.status(500).json('There seems to have been an issue:', e);
      }
    }
    if (!relatedProductsSection && productAttributes) {
      retrieveSimilarProducts();
    }
  }, [additionalSections, relatedProductsSection, productAttributes, locale]);

  useEffect(() => {
    if (shopifyProducts?.length) {
      shopifyProducts.forEach((shopifyProduct, index) => {
        setTimeout(() => {
          trackKlavioProductView({
            id: parseShopifyGid(shopifyProduct.productId),
            title: shopifyProduct.productTitle,
            imageUrl: products[index]?.thumbnailImage?.baseUrl,
            price: shopifyProduct.productPrice,
            compareAtPrice: shopifyProduct.productCompareAtPrice,
            vendor: shopifyProduct?.productVendor,
            specs: data.productsCollection?.items?.[index]?.productSpecsCollection,
          });
        }, index * 1000);
      });
    }
  }, [shopifyProducts]);

  return (
    <>
      <section>
        <div className={styles.productPage}>
          <ProductGallery
            thumbnail={thumbnailImage}
            productPictures={productPictures}
            productVideos={productVideos}
          />
          <div className={styles.productInfo}>
            <ProductBundleInfo
              locale={locale}
              bundlePrices={combinedProductsPrices}
              shopifyData={shopifyProducts}
              resources={resources}
              productBundleData={data}
            />
            <div className={styles.topDivider} />
            <div className={styles.productExpanders}>
              <Expander
                data={{
                  title: ingredientsTitle?.text,
                  content: data.ingredients,
                }}
              />
              <Expander
                data={{
                  title: directionOfUseTitle?.text,
                  content: data.directionOfUse,
                }}
              />
              {data.expandersCollection.items.map(expander => (
                <Expander data={expander} />
              ))}
            </div>
            {disclaimerText?.text && (
              <RichText className={styles.disclaimerText} text={disclaimerText.text.json} />
            )}
            <div className={styles.bottomDivider} />
          </div>
        </div>
        {reviewProducts.length === 1 && (
          <EvesReviews
            eanNumber={reviewProducts[0]?.shopifyData?.variants?.[0]?.sku}
            locale={locale}
            title={reviewsTitle.text}
          />
        )}
        {renderSections('', locale, otherSections, undefined, resources)}
        {relatedProductsSection && (
          <RelatedProducts
            data={relatedProductsSection}
            locale={locale}
            resources={resources}
            className={styles.relatedProductsSection}
          />
        )}
        {automaticRelatedProducts.length > 0 && (
          <RelatedProducts
            data={{
              name: 'Automatically Generated Related Products',
              title: relatedProductsDefaultTitle.text,
              relatedProducts: automaticRelatedProducts,
              type: 'SectionRelatedProducts',
            }}
            locale={locale}
            resources={resources}
          />
        )}
      </section>
    </>
  );
};

export default PageProductBundle;
